import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { toast } from "@bdo/cornerstone-ui";
import { useAppDispatch, useAuth } from "@/hooks";
import {
  closeManageAccess,
  selectIsManageAccessOpen,
  selectManageAccessWorkflowInstanceId,
} from "@/features/overlays/manageAccessSlice";
import {
  useGetAccessControlEntriesQuery,
  useAddUsersMutation,
  useRemoveUsersMutation,
  useGetWorkflowInstanceByIdQuery,
  useSearchValidUsersMutation,
  useSubscribeNotificationsMutation,
  useUnsubscribeNotificationsMutation,
  type AccessControlEntry,
  type ValidUser,
} from "@/app/workflows/workflowInstancesApi";

export function useManageAccess() {
  const dispatch = useAppDispatch();
  const isOpen = useSelector(selectIsManageAccessOpen);
  const workflowInstanceId = useSelector(selectManageAccessWorkflowInstanceId);
  const { t } = useTranslation();
  const { user } = useAuth();
  const searchFormResetRef = useRef<(() => void) | null>(null);

  const [addError, setAddError] = useState<string | null>(null);
  const [searchResults, setSearchResults] = useState<ValidUser[]>([]);
  const [temporaryAddedUsers, setTemporaryAddedUsers] = useState<ValidUser[]>(
    [],
  );
  const [usersToDelete, setUsersToDelete] = useState<string[]>([]);
  const [notificationOverrides, setNotificationOverrides] = useState<
    Record<string, boolean>
  >({});

  const { data: workflowInstance } = useGetWorkflowInstanceByIdQuery(
    { id: workflowInstanceId || "", select: ["workflowInstanceName"] },
    { skip: !workflowInstanceId || !isOpen },
  );

  const {
    data: entries = [],
    isLoading,
    isFetching,
    isError,
    refetch,
  } = useGetAccessControlEntriesQuery(
    { workflowInstanceId: workflowInstanceId || "" },
    { skip: !workflowInstanceId || !isOpen },
  );

  const [searchValidUsers, { isLoading: isSearching }] =
    useSearchValidUsersMutation();
  const [addUsers, { isLoading: isAdding }] = useAddUsersMutation();
  const [removeUsers, { isLoading: isRemoving }] = useRemoveUsersMutation();
  const [subscribeNotifications] = useSubscribeNotificationsMutation();
  const [unsubscribeNotifications] = useUnsubscribeNotificationsMutation();

  function handleClose() {
    dispatch(closeManageAccess());
    searchFormResetRef.current?.();
    setAddError(null);
    setSearchResults([]);
    setTemporaryAddedUsers([]);
    setUsersToDelete([]);
    setNotificationOverrides({});
  }

  function setIsOpen(open: boolean) {
    if (!open) {
      handleClose();
    }
  }

  useEffect(() => {
    if (isOpen && workflowInstanceId) {
      refetch();
    }
  }, [isOpen, workflowInstanceId, refetch]);

  const handleSearch = useCallback(
    (query: string) => {
      if (!query.trim() || !workflowInstanceId) {
        setSearchResults([]);
        return;
      }

      searchValidUsers({
        workflowInstanceId,
        searchUsers: query,
      })
        .unwrap()
        .then((results) => {
          setSearchResults(results);
          if (results.length === 0) {
            toast.info({
              title: t("manageAccess.searchInput.noResults", {
                defaultValue: "No users found",
              }),
              message: t("manageAccess.searchInput.noResultsMessage", {
                defaultValue:
                  "No users matched your search. Please try a different query.",
              }),
              duration: 5000,
            });
          }
        })
        .catch(() => {
          toast.error({
            title: t("manageAccess.errors.searchFailed", {
              defaultValue: "Search failed",
            }),
            message: t("manageAccess.errors.searchFailedMessage", {
              defaultValue: "Unable to search for users. Please try again.",
            }),
            duration: 10000,
          });
          setSearchResults([]);
        });
    },
    [workflowInstanceId, searchValidUsers, t],
  );

  function handleSelectUser(selectedUser: ValidUser) {
    if (!temporaryAddedUsers.some((u) => u.email === selectedUser.email)) {
      setTemporaryAddedUsers([...temporaryAddedUsers, selectedUser]);
    }
  }

  const filteredSearchResults = searchResults.filter(
    (result) =>
      !entries.some((entry) => entry.email === result.email) &&
      !temporaryAddedUsers.some((tempUser) => tempUser.email === result.email),
  );

  async function handleSaveChanges() {
    if (!workflowInstanceId) return;
    if (temporaryAddedUsers.length === 0 && usersToDelete.length === 0) return;

    const hasUsersToAdd = temporaryAddedUsers.length > 0;
    const hasUsersToDelete = usersToDelete.length > 0;

    try {
      if (hasUsersToAdd) {
        await addUsers({
          workflowInstanceId,
          userEmails: temporaryAddedUsers.map((u) => u.email),
        }).unwrap();

        toast.success({
          title: t("manageAccess.notification.addUserSuccess.title", {
            defaultValue: "User added",
          }),
          message: t("manageAccess.notification.addUserSuccess.message", {
            defaultValue:
              "The user has been successfully added to the workflow.",
          }),
          duration: 3000,
        });
      }
    } catch {
      toast.error({
        title: t("manageAccess.notification.addUserError.title", {
          defaultValue: "Unable to add user",
        }),
        message: t("manageAccess.notification.addUserError.message", {
          defaultValue:
            "There was a problem adding the user to the workflow. Please try again.",
        }),
        duration: 10000,
      });
      setAddError(
        t("manageAccess.notification.addUserError.message", {
          defaultValue:
            "There was a problem adding the user to the workflow. Please try again.",
        }),
      );
      return;
    }

    try {
      if (hasUsersToDelete) {
        await removeUsers({
          workflowInstanceId,
          userEmails: usersToDelete,
        }).unwrap();

        toast.success({
          title: t("manageAccess.notification.removeUserSuccess.title", {
            defaultValue: "User removed",
          }),
          message: t("manageAccess.notification.removeUserSuccess.message", {
            defaultValue:
              "The user has been successfully removed from the workflow.",
          }),
          duration: 3000,
        });
      }
    } catch {
      toast.error({
        title: t("manageAccess.notification.removeUserError.title", {
          defaultValue: "Unable to remove user",
        }),
        message: t("manageAccess.notification.removeUserError.message", {
          defaultValue:
            "There was a problem removing the user from the workflow. Please try again.",
        }),
        duration: 10000,
      });
      setAddError(
        t("manageAccess.notification.removeUserError.message", {
          defaultValue:
            "There was a problem removing the user from the workflow. Please try again.",
        }),
      );
      return;
    }

    searchFormResetRef.current?.();
    setSearchResults([]);
    setTemporaryAddedUsers([]);
    setUsersToDelete([]);
    setAddError(null);
  }

  async function handleToggleNotifications(entry: AccessControlEntry) {
    if (!workflowInstanceId) return;

    const currentStatus =
      notificationOverrides[entry.email] ?? entry.isNotificationEnabled;
    const newStatus = !currentStatus;

    setNotificationOverrides((prev) => ({
      ...prev,
      [entry.email]: newStatus,
    }));

    try {
      if (currentStatus) {
        await unsubscribeNotifications({
          workflowInstanceId,
          userEmail: entry.email,
        }).unwrap();
      } else {
        await subscribeNotifications({
          workflowInstanceId,
          userEmail: entry.email,
        }).unwrap();
      }

      toast.success({
        title: t("manageAccess.notification.muteSuccess.title", {
          defaultValue: "Notifications updated",
        }),
        message: t("manageAccess.notification.muteSuccess.message", {
          defaultValue: "Your preferences have been saved successfully.",
        }),
        duration: 3000,
      });
    } catch {
      setNotificationOverrides((prev) => ({
        ...prev,
        [entry.email]: currentStatus,
      }));

      toast.error({
        title: t("manageAccess.errors.notificationToggleFailed", {
          defaultValue: "Failed to update notification settings",
        }),
        message: t("manageAccess.errors.notificationToggleFailedMessage", {
          defaultValue:
            "Unable to update notification preferences. Please try again.",
        }),
        duration: 10000,
      });
    }
  }

  function handleDeleteUser(entry: AccessControlEntry) {
    if (!workflowInstanceId) return;

    if (usersToDelete.includes(entry.email)) {
      setUsersToDelete(usersToDelete.filter((email) => email !== entry.email));
    } else {
      setUsersToDelete([...usersToDelete, entry.email]);
    }
  }

  function handleRemoveTemporaryUser(email: string) {
    setTemporaryAddedUsers(
      temporaryAddedUsers.filter((u) => u.email !== email),
    );
  }

  return {
    isOpen,
    setIsOpen,
    workflowInstance,
    entries,
    isLoading: isLoading || isFetching,
    isError,
    isSearching,
    isAdding,
    isRemoving,
    addError,
    usersToDelete,
    notificationOverrides,
    temporaryAddedUsers,
    filteredSearchResults,
    searchFormResetRef,
    currentUserEmail: user?.email,
    handleClose,
    handleSearch,
    handleSelectUser,
    handleSaveChanges,
    handleToggleNotifications,
    handleDeleteUser,
    handleRemoveTemporaryUser,
    setAddError,
  };
}
